import Dexie from "dexie"
import { } from "./locale"
import { api } from "./api"
import { app } from "./app"
import { previewSiteId } from "./smartcms/preview"

export class DB extends Dexie {

  zendeskSections!: Dexie.Table<ZendeskSection, number>
  zendeskCategories!: Dexie.Table<ZendeskCategory, number>
  articles!: Dexie.Table<ZendeskArticle, number>
  services!: Dexie.Table<Service, number>
  providers!: Dexie.Table<Provider, number>

  constructor() {
    // Namespaced per preview site: `loadLocalServices` reads the whole table
    // with no site filter, so a shared database would cross-paint content.
    const preview = previewSiteId()
    super(preview ? `db:${preview}` : "db")
    this.version(2).stores({
      zendeskCategories: 'id',
      zendeskSections: 'id',
      articles: 'id',

      services: 'id, date_created, date_updated',
      providers: 'id',
      attachments: 'articleId'
    })

    this.articles = this.table("articles")
    this.zendeskCategories = this.table("zendeskCategories")
    this.zendeskSections = this.table("zendeskSections")

    this.services = this.table("services")
    this.providers = this.table("providers")
  }

  async loadLocalServices(): Promise<number> {
    const dbs = (await this.services.toArray()) || []
    if (dbs.length > 0) {
      const r = dbs.reduce((a, b) => { a[b.id] = b; return a }, {})
      app.data.services = r as any
    }
    console.log("Local Services Found: ", dbs.length)
    return dbs.length
  }

  async loadLocalProviders(): Promise<number> {
    console.log("Loading providers...")
    const dbs = (await this.providers.toArray()) || []
    if (dbs.length > 0) {
      const r = dbs.reduce((a, b) => { a[b.id] = b; return a }, {})
      app.data.providers = r as any
    }
    console.log("Local Providers Found: ", dbs.length)
    return dbs.length
  }

  async loadZendeskContent(): Promise<number> {

    const cats = (await this.zendeskCategories.toArray()) || []
    if (cats.length > 0) {
      const r = cats.reduce((a, b) => { a[b.id] = b; return a }, {})
      app.data.zendesk.categories = r as any
    }

    const sects = (await this.zendeskSections.toArray()) || []
    if (sects.length > 0) {
      const r = sects.reduce((a, b) => { a[b.id] = b; return a }, {})
      app.data.zendesk.sections = r as any
    }

    const arts = (await this.articles.toArray()) || []
    if (arts.length > 0) {
      const r = arts.reduce((a, b) => { a[b.id] = b; return a }, {})
      app.data.zendesk.articles = r as any
    }
    return arts.length
  }

  async fetchService(id: number) {
    console.log('Fetching Service...')

    /*
     * The in-memory copy is authoritative; the Directus endpoint would look up
     * a Smart CMS id in another database and delete the row on the miss. The
     * on-demand load covers deep links that arrive before the store is filled.
     */
    if (app.siteId) {
      const cached = app.data.services[id]
      if (cached) return cached

      const { loadServiceById } = await import("./smartcms")
      const service = await loadServiceById(app.siteId, id)
      if (!service) {
        console.log('Service Not Found')
        return
      }

      app.data.services[service.id] = service
      try { await this.services.put(service, service.id) } catch (error) { }
      app.update()
      console.log('Service Loaded (Smart CMS)')
      return service
    }

    const service = await api.getService(id)
    if (service === null) {
      app.data.services[id] = null
      this.services.delete(id)
      app.update()
      console.log('Service Not Found');
      return
    } else if (service) {
      this.services.put(service, service.id)
      app.data.services[service.id] = service
      app.update()
      console.log('Service Updated');
      return service
    } else {
      return app.data.services[id]
    }
  }

  async fetchArticle(country: number, id: number) {
    console.log('Fetching Article...')

    /*
     * The article body is already in the row the tree loaded, so there is no
     * per-article endpoint. The on-demand load covers a deep link that arrives
     * before the tree is mapped, which would otherwise render "page does not
     * exist" for an article that exists.
     */
    if (app.siteId) {
      const cached = app.data.zendesk.articles[id]
      if (cached) return cached

      const { loadArticleById } = await import("./smartcms")
      const article = await loadArticleById(app.siteId, id)
      if (!article) {
        console.log('Article Not Found')
        return
      }

      app.data.zendesk.articles[article.id] = article
      // Cache it so a later navigation to the same article is instant.
      try { await this.articles.put(article, article.id) } catch (error) { }
      app.update()
      console.log('Article Loaded (Smart CMS)')
      return article
    }

    const article = await api.getArticle(country, id)
    if (article === null) {
      app.data.zendesk.articles[id] = null
      this.articles.delete(id)
      app.update()
      console.log('Article Not Found');
      return
    } else if (article) {
      const updatedArticle: ZendeskArticle = {
        category: app.data.zendesk.articles[id]?.category,
        section: app.data.zendesk.articles[id]?.section,
        id: article.id,
        description: article.description,
        name: article.name,
        updated_at: article.updated_at
      }
      this.articles.put(updatedArticle, article.id)
      app.data.zendesk.articles[article.id] = updatedArticle
      app.update()
      console.log('Article Updated');
      return updatedArticle
    } else {
      return app.data.zendesk.articles[id]
    }
  }

  async updateServices() {
    console.log("Updating Database...")

    const dbs = (await this.services.toArray()) || []

    let lastUpdate = 0
    for (const s of dbs) {
      if (s.date_updated > lastUpdate) lastUpdate = s.date_updated
      if (s.date_created > lastUpdate) lastUpdate = s.date_created
    }
    console.log("Last Update: ", lastUpdate)

    //ToDo: Restore in the future until is stable enough
    // const services = await api.getServices(app.country, lastUpdate)
    const services = await api.getServices(app.country)

    if (services) {
      const localServices = {}
      console.log(`Saving ${services.length} Services`)
      for (const service of services) {
        localServices[service.id] = service
      }
      await this.services.clear()
      for (const service of services) {
        await this.services.put(service, service.id)
      }
      app.data.services = localServices
      app.state.servicesLoaded = true
      app.update()
    }
    console.log("Database Updated!")
  }

  async updateProviders() {
    console.log("Updating Providers Database...")
    app.data.providers ||= {}
    const providers = await api.getProviders(app.country)
    if (providers) {
      for (const provider of providers) {
        app.data.providers[provider.id] = provider
      }
      app.update()
      console.log(`Saving ${providers.length} Providers`)
      for (const provider of providers) {
        await this.providers.put(provider, provider.id)
      }
    }
    console.log("Providers Database Updated!")
  }

  async ensureProviders() {
    if (Object.keys(app.data.providers).length > 0) return
    await this.loadLocalProviders()
    if (Object.keys(app.data.providers).length > 0) return

    // A cold deep link reaches this before providers load, and the page reads
    // the provider name from the store, so it needs its own load.
    if (app.siteId) {
      const { loadProviders } = await import("./smartcms")
      const providers = await loadProviders(app.siteId)
      if (!providers) return
      app.data.providers = providers
      try {
        for (const provider of Object.values(providers)) {
          await this.providers.put(provider, provider.id)
        }
      } catch (error) { }
      app.update()
      console.log("Providers Loaded (Smart CMS)")
      return
    }

    await this.updateProviders()
  }

  async updateArticles() {
    console.log("Updating Articles Database...")
    const content = await api.getArticles(app.country)

    if (content) {

      const categories = {}
      const sections = {}
      const articles = {}

      await this.zendeskCategories.clear()
      await this.zendeskSections.clear()
      await this.articles.clear()

      console.log(`Saving Articles`)

      for (const key in content) {
        const cat = content[key]
        categories[cat.id] = cat
        await this.zendeskCategories.put(cat, cat.id)
        for (const key in cat.sections) {
          const sec = cat.sections[key]
          sec.category = cat.id
          sections[sec.id] = sec
          await this.zendeskSections.put(sec, sec.id)
          for (const key in sec.articles) {
            const art = sec.articles[key]
            art.section = sec.id
            art.category = cat.id
            articles[art.id] = art
            await this.articles.put(art, art.id)
          }
        }
      }

      app.data.zendesk.categories = categories
      app.data.zendesk.sections = sections
      app.data.zendesk.articles = articles

      app.update()
    }

    app.state.articlesLoaded = true
    console.log("Articles Database Updated!")
  }

}
